import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiMenu, FiX, FiLogOut, FiHome, FiCamera,
  FiFileText, FiClipboard, FiSettings, FiUsers,
  FiImage, FiMonitor, FiDollarSign, FiShield,
  FiUserCheck, FiTag, FiCreditCard, FiShoppingCart,
  FiHome as FiBuilding, FiUserPlus, FiBarChart2,
  FiCode, FiActivity, FiCheckCircle, FiGitMerge, FiAlertTriangle, FiClipboard as FiInventory,
  FiBookOpen, FiKey, FiList, FiHelpCircle, FiUploadCloud, FiUser
} from 'react-icons/fi';
import { MARCA, descritorDaVertical } from '../data/marca';
import useTituloDaVertical from '../hooks/useTituloDaVertical';
import CameraSignalBanner from './CameraSignalBanner';
import '../styles/Layout.css';
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout, can, vertical, clienteNome } = useAuth();
  // Mantém a aba do navegador coerente com a instalação.
  useTituloDaVertical();
  const location = useLocation();
  // ─── Grupos de menu ─────────────────────────────────────────────────────
  const menuGroups = [
    {
      label: 'Operação',
      items: [
        { path: '/dashboard',         label: 'Dashboard',       icon: FiHome, feature: 'dashboard' },
        { path: '/monitoring',        label: 'Monitoramento',   icon: FiMonitor, feature: 'monitoring' },
        { path: '/cameras',           label: 'Câmeras',         icon: FiCamera, feature: 'cameras_view' },
        { path: '/plate-recognition', label: 'Reconhecimento',  icon: FiImage, feature: 'plate_recognition' },
        { path: '/tickets',           label: 'Tickets',         icon: FiClipboard, feature: 'tickets' },
        { path: '/pdv',               label: 'Caixa (PDV)',     icon: FiShoppingCart, feature: 'pdv' },
        { path: '/reports',           label: 'Relatórios',      icon: FiFileText, feature: 'reports_operation' },
        { path: '/gate-log',           label: 'Log de Cancelas',  icon: FiActivity, feature: 'gate_log' },
        // Onda 183 (clube e condomínio). Onda 197: no condomínio a tela é a Movimentação — entradas e saídas.
        { path: '/saidas',             label: String(vertical || '').startsWith('condominio') ? 'Movimentação' : 'Saídas', icon: FiLogOut, feature: 'exit_release' },
        { path: '/ocr-quality',        label: 'Qualidade do OCR', icon: FiCheckCircle, feature: 'ocr_quality' },
        { path: '/reconciliation',     label: 'Reconciliação',    icon: FiGitMerge, feature: 'reconciliation' },
        { path: '/quarantine',         label: 'Quarentena',       icon: FiAlertTriangle, feature: 'quarantine' },
        { path: '/inventory',          label: 'Inventário do Pátio', icon: FiInventory, feature: 'yard_inventory' },
      ]
    },
    {
      label: 'Cadastros',
      items: [
        { path: '/customers',     label: 'Clientes',      icon: FiUsers, feature: 'customers' },
        { path: '/monthly-plans', label: 'Mensalistas',   icon: FiCreditCard, feature: 'monthly_plans' },
        { path: '/accredited',    label: 'Credenciados',  icon: FiShield, feature: 'accredited' },
        { path: '/agreements',    label: 'Convênios',     icon: FiTag, feature: 'agreements' },
      ]
    },
    {
      // Onda 108b: o título do grupo é o descritor da INSTALAÇÃO. Os itens já
      // eram filtrados por vertical desde a Onda 43, mas o título ficou fixo em
      // "Condomínio" — e no Ipê, que é `clube_social`, "Visitantes" e
      // "Relatórios de Acesso" apareciam sob o nome de outro negócio.
      label: descritorDaVertical(vertical) || 'Acessos',
      items: [
        // Onda 110: o cadastro do clube ganhou tela. Cada vertical mostra o
        // seu: "Associados" aqui, "Moradores" no condomínio — o catálogo de
        // permissões é que decide qual aparece.
        { path: '/club-members',   label: 'Associados',          icon: FiUsers, feature: 'club_members' },
        { path: '/residents',      label: 'Moradores',           icon: FiBuilding, feature: 'residents' },
        { path: '/visitors',       label: 'Visitantes',          icon: FiUserPlus, feature: 'visitors' },
        { path: '/access-reports', label: 'Relatórios de Acesso', icon: FiBarChart2, feature: 'access_reports' },
      ]
    },
    {
      // Onda 64: o grupo deixou de ser "Clube Social". A tela de Integração e
      // Outbox serve às duas verticais desde a Onda 57b, e a documentação vale
      // para quem escreve o integrador de qualquer uma delas.
      label: 'Integração',
      items: [
        // Onda 65: "Chaves de API" veio de Administração para cá. As três telas
        // são o mesmo assunto em três atos — a credencial que o integrador usa,
        // o destino dos eventos que enviamos, e o contrato que ele precisa
        // seguir —, e estavam em grupos diferentes por acidente de história: a
        // de chaves é anterior à unificação das verticais.
        { path: '/api-integration',  label: 'Chaves de API',              icon: FiKey, feature: 'api_integration' },
        { path: '/club-social',      label: 'Integração e Outbox',        icon: FiCode, feature: 'club_social' },
        // Onda 66: o log tem entrada própria porque tem permissão própria. Quem
        // escreve o integrador chega aqui sem passar pela tela de chaves, que
        // continua sendo de superadministrador. Para o superadmin há dois
        // caminhos para a mesma tabela — este e a aba em Chaves de API —, e isso
        // é de propósito: tirar a aba faria o superadmin perder o lado a lado
        // entre a chave e as chamadas que ela fez.
        { path: '/api-request-log',  label: 'Log de Requisições',         icon: FiList, feature: 'api_request_log' },
        // Onda 109: o log conta requisição por requisição; esta tela conta a
        // CARGA — o que entrou, o que foi recusado e por quê. São perguntas
        // diferentes, e quem responde a segunda é o gestor, não o integrador.
        { path: '/integration-loads', label: 'Cargas da Integração',      icon: FiUploadCloud, feature: 'integration_loads' },
        { path: '/integration-docs', label: 'Documentação da Integração', icon: FiBookOpen, feature: 'integration_docs' },
      ]
    },
    {
      label: 'Administração',
      items: [
        { path: '/price-tables',    label: 'Tarifas',           icon: FiDollarSign, feature: 'price_tables' },
        { path: '/users',           label: 'Usuários',          icon: FiUserCheck, feature: 'users_manage' },
        { path: '/settings',        label: 'Configurações',     icon: FiSettings, feature: 'settings_view' },
      ]
    },
    {
      // Onda 92: por último de propósito. A Ajuda não é uma etapa do trabalho —
      // é onde se recorre quando o trabalho trava. Pô-la no meio da Operação
      // competiria com as telas que o operador usa o dia inteiro; no fim, ela
      // fica no lugar onde os olhos procuram quando já não sabem o que clicar.
      label: 'Ajuda',
      items: [
        { path: '/ajuda', label: 'Central de Ajuda', icon: FiHelpCircle, feature: 'help_center' },
      ]
    }
  ];
  return (
    <div className="layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          {/* A marca é uma só; o descritor diz em qual instalação estamos. Sem
              ele, o Ipê Clube exibia o nome de um estacionamento. */}
          <h1 className="marca">
            <img src={`${process.env.PUBLIC_URL}/marca/icone.svg`} alt="" className="marca-icone" />
            <span className="marca-texto">
              {MARCA}
              {/* Onda 123: QUAL cliente — em destaque, entre a marca e a vertical. */}
              {clienteNome && (
                <span className="marca-cliente" title={clienteNome}>{clienteNome}</span>
              )}
              {descritorDaVertical(vertical) && (
                <small className="marca-vertical">{descritorDaVertical(vertical)}</small>
              )}
            </span>
          </h1>
          <button className="toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuGroups.map(group => {
            // Onda 43: o item aparece quando a funcionalidade existe nesta
            // vertical e o perfil a alcança; o grupo some quando fica vazio.
            // Antes era `adminOnly` binário, e num estacionamento o admin via
            // os grupos Condomínio e Clube Social, abria as telas e o backend
            // respondia normalmente.
            const itensVisiveis = group.items.filter(item => !item.feature || can(item.feature));
            if (itensVisiveis.length === 0) return null;
            return (
              <div key={group.label} className="nav-group">
                {sidebarOpen && (
                  <span className="nav-group-label">{group.label}</span>
                )}
                {itensVisiveis.map(item => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-item ${isActive ? 'active' : ''}`}
                      title={!sidebarOpen ? item.label : undefined}
                    >
                      <Icon />
                      {sidebarOpen && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            {sidebarOpen && (
              <>
                <p className="user-name">{user?.username}</p>
                <p className="user-role">{user?.role_label || user?.role}</p>
              </>
            )}
          </div>
          {/* Onda 150: a conta de quem está logado — dados e troca da própria senha. */}
          <Link to="/minha-conta" className="logout-btn layout-conta-btn" title="Minha conta">
            <FiUser />
            {sidebarOpen && <span>Minha conta</span>}
          </Link>
          <button className="logout-btn" onClick={logout} title="Sair">
            <FiLogOut />
            {sidebarOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>
      <main className="main-content">
        {/* Onda 115: câmera sem imagem, em qualquer tela — fora da área que rola. */}
        <CameraSignalBanner />
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};
export default Layout;
